import { useTenantMappings } from '@vp/ubik-context'

import { useTenant } from './useTenant'

import { useAuthorization } from '../components/root/contexts/AuthorizationProvider'

/**
 * Derives the CartManager tenant (e.g. 'vistaprint' -> 'vistaprint-prod').
 *
 * The add-to-cart write path still goes through CartManager.
 */
const buildCartManagerTenant = (tenant: string): string => `${tenant.toLowerCase()}-prod`

export type CartIdentifiers = {
  shopperId: string | undefined
  /** CCv2 key account id, sourced from the CEP tenant registry. Used by the CCv2 Cart API. */
  accountId: string
  /** CCv2 cart type space id, sourced from the CEP tenant registry. Used by the CCv2 Cart API. */
  cartTypeId: string
  /** CartManager tenant (e.g. 'vistaprint-prod'). */
  cartManagerTenant: string
}

/**
 * Single source of truth for the identifiers used by cart operations.
 *
 * The CCv2 read identifiers (`accountId`, `cartTypeId`) are sourced from the CEP
 * tenant registry via `useTenantMappings()`. The CartManager write tenant is
 * derived from `useTenant()`.
 */
export const useCartIdentifiers = (): CartIdentifiers => {
  const { shopperId } = useAuthorization()
  const { account: accountId, defaultCartTypeId: cartTypeId } = useTenantMappings()
  const tenant = useTenant()

  return {
    shopperId,
    accountId,
    cartTypeId,
    cartManagerTenant: buildCartManagerTenant(tenant),
  }
}
